import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UsdCurrencyPipe } from '../pipes/usd-currency.pipe';

export interface RecordPaymentDialogData {
  insuredName: string;
  monthlyPayment?: number | null;
}

/** What the dialog hands back to the caller on submit (undefined on cancel). */
export interface RecordPaymentDialogResult {
  amount: number;
  /** Teddy #10 · the enum name the server parses (DownPayment | MonthlyPayment | Payoff). */
  paymentType: string;
  note?: string;
}

/** Teddy #10 · payment types a producer can log for an off-Stripe payment. */
export const MANUAL_PAYMENT_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'MonthlyPayment', label: 'Monthly payment' },
  { value: 'DownPayment', label: 'Down payment' },
  { value: 'Payoff', label: 'Payoff' }
];

/**
 * KAN-41 · Small form dialog for logging a payment collected outside of Stripe.
 * Pure form: it validates (amount required, > 0; optional note) and returns the
 * payload — the parent performs the recordPayment call, snackbar and reload so
 * all servicing calls stay in one place. Fully token-driven (--ifq-* vars).
 */
@Component({
  selector: 'app-record-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    UsdCurrencyPipe
  ],
  template: `
    <div class="rp-container">
      <h2 mat-dialog-title class="rp-title">
        <mat-icon aria-hidden="true">point_of_sale</mat-icon>
        Record manual payment
      </h2>

      <mat-dialog-content>
        <p class="rp-sub">
          Log a payment collected outside of Stripe for
          <strong>{{ data.insuredName }}</strong>. This records the payment
          against the account balance.
        </p>

        <form [formGroup]="form" class="rp-form" (ngSubmit)="onSubmit()">
          <mat-form-field appearance="outline" class="rp-full">
            <mat-label>Amount</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input matInput
                   type="number"
                   formControlName="amount"
                   min="0.01"
                   step="0.01"
                   inputmode="decimal"
                   placeholder="0.00"
                   required>
            <mat-hint *ngIf="data.monthlyPayment">Monthly payment is {{ data.monthlyPayment | usdCurrency }}</mat-hint>
            <mat-error *ngIf="form.get('amount')?.hasError('required')">Amount is required</mat-error>
            <mat-error *ngIf="form.get('amount')?.hasError('min')">Enter an amount greater than zero</mat-error>
          </mat-form-field>

          <!-- Teddy #10 · let the producer classify the manual payment -->
          <mat-form-field appearance="outline" class="rp-full">
            <mat-label>Payment type</mat-label>
            <mat-select formControlName="paymentType">
              <mat-option *ngFor="let t of paymentTypes" [value]="t.value">{{ t.label }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="rp-full">
            <mat-label>Note (optional)</mat-label>
            <textarea matInput
                      formControlName="note"
                      rows="2"
                      placeholder="e.g. Check #1042 received 07/01"></textarea>
          </mat-form-field>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end" class="rp-actions">
        <button mat-button type="button" (click)="onCancel()">Cancel</button>
        <button mat-raised-button
                color="primary"
                type="button"
                (click)="onSubmit()"
                [disabled]="form.invalid">
          <mat-icon aria-hidden="true">check</mat-icon>
          Record payment
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .rp-container {
      min-width: 380px;
      max-width: 460px;
      font-family: var(--ifq-body);
      color: var(--ifq-ink);
    }

    .rp-title {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 6px;
      font-family: var(--ifq-disp);
      font-weight: 600;
      color: var(--ifq-ink);
    }

    .rp-title mat-icon {
      color: light-dark(var(--ifq-gold-deep), var(--ifq-gold-hi));
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .rp-sub {
      margin: 0 0 18px;
      color: var(--ifq-muted);
      line-height: 1.5;
      font-size: 14px;
    }

    .rp-sub strong {
      color: var(--ifq-ink);
    }

    .rp-form {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .rp-full {
      width: 100%;
    }

    .rp-actions {
      padding: 8px 0 0 0;
      margin: 0;
      gap: 8px;
    }
  `]
})
export class RecordPaymentDialogComponent {
  form: FormGroup;
  readonly paymentTypes = MANUAL_PAYMENT_TYPES;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RecordPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RecordPaymentDialogData
  ) {
    this.form = this.fb.group({
      amount: [null, [Validators.required, Validators.min(0.01)]],
      paymentType: ['MonthlyPayment', Validators.required],
      note: ['']
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const amount = Number(this.form.value.amount);
    const noteRaw = (this.form.value.note || '').toString().trim();
    const result: RecordPaymentDialogResult = {
      amount,
      paymentType: this.form.value.paymentType || 'MonthlyPayment'
    };
    if (noteRaw) {
      result.note = noteRaw;
    }
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
